/** DISCO companies enabled for code-10 admin — display order: MEPCO, FESCO, IESCO */
export const ENABLED_DISCO_COMPANY_CODES = ["15", "13", "14"];

export const DEFAULT_DISCO_COMPANY_CODE = "15";

export const SELECTED_ADMIN_COMPANY_STORAGE_KEY = "selectedAdminCompanyCode";
export const LAST_ADMIN_COMPANY_PREF_KEY = "lastAdminCompanyPreference";

export const readLastAdminCompanyPreference = () => {
  try {
    return localStorage.getItem(LAST_ADMIN_COMPANY_PREF_KEY) || "";
  } catch {
    return "";
  }
};

export const writeLastAdminCompanyPreference = (companyCode) => {
  try {
    if (companyCode) {
      localStorage.setItem(LAST_ADMIN_COMPANY_PREF_KEY, String(companyCode));
    }
  } catch {
    // ignore storage errors
  }
};

export const readPersistedAdminCompanyCode = () => {
  try {
    return localStorage.getItem(SELECTED_ADMIN_COMPANY_STORAGE_KEY) || "";
  } catch {
    return "";
  }
};

export const writePersistedAdminCompanyCode = (companyCode) => {
  try {
    if (companyCode) {
      localStorage.setItem(SELECTED_ADMIN_COMPANY_STORAGE_KEY, String(companyCode));
      return;
    }
    localStorage.removeItem(SELECTED_ADMIN_COMPANY_STORAGE_KEY);
  } catch {
    // ignore storage errors
  }
};

export const clearPersistedAdminCompanyCode = () => {
  writePersistedAdminCompanyCode("");
};

export const ADMIN_COMPANY_SESSION_KEY = "adminCompanySessionActive";
export const ADMIN_COMPANY_PROMPT_PENDING_KEY = "adminCompanyPromptPending";

export const markAdminCompanySessionActive = () => {
  try {
    sessionStorage.setItem(ADMIN_COMPANY_SESSION_KEY, "1");
  } catch {
    // ignore storage errors
  }
};

export const isAdminCompanySessionActive = () => {
  try {
    return sessionStorage.getItem(ADMIN_COMPANY_SESSION_KEY) === "1";
  } catch {
    return false;
  }
};

export const clearAdminCompanySession = () => {
  try {
    sessionStorage.removeItem(ADMIN_COMPANY_SESSION_KEY);
  } catch {
    // ignore storage errors
  }
};

export const markAdminCompanyPromptPending = () => {
  try {
    sessionStorage.setItem(ADMIN_COMPANY_PROMPT_PENDING_KEY, "1");
  } catch {
    // ignore storage errors
  }
};

export const clearAdminCompanyPromptPending = () => {
  try {
    sessionStorage.removeItem(ADMIN_COMPANY_PROMPT_PENDING_KEY);
  } catch {
    // ignore storage errors
  }
};

export const isAdminCompanyPromptPending = () => {
  try {
    return sessionStorage.getItem(ADMIN_COMPANY_PROMPT_PENDING_KEY) === "1";
  } catch {
    return false;
  }
};

export const isBrowserPageReload = () => {
  try {
    const navigationEntry = performance.getEntriesByType("navigation")[0];
    return navigationEntry?.type === "reload";
  } catch {
    return false;
  }
};

export const clearAdminCompanySelection = () => {
  clearPersistedAdminCompanyCode();
  clearAdminCompanySession();
  clearAdminCompanyPromptPending();
};

export const readRestorableAdminCompanyCode = () => {
  if (!isAdminCompanySessionActive()) {
    return "";
  }
  return readPersistedAdminCompanyCode();
};

export const isEnabledDiscoCompany = (companyCode) =>
  ENABLED_DISCO_COMPANY_CODES.includes(String(companyCode));

export const sortEnabledDiscoCompanies = (companies = []) => {
  const orderMap = Object.fromEntries(
    ENABLED_DISCO_COMPANY_CODES.map((code, index) => [code, index]),
  );

  return [...companies]
    .filter((company) => isEnabledDiscoCompany(company.companyCode))
    .sort((left, right) => {
      const leftOrder = orderMap[String(left.companyCode)] ?? 999;
      const rightOrder = orderMap[String(right.companyCode)] ?? 999;
      return leftOrder - rightOrder;
    });
};

/** PITC HQ admin — company code 10, role 1151 */
export const isPitcAdminUser = (code, roleId) =>
  String(code) === "10" && String(roleId) === "1151";

/** DISCO-level admin — company code 10, selects a distribution company */
export const isDiscoAdminUser = (code, roleId) =>
  String(code) === "10" && String(roleId) !== "1151";

/** Company code used for subdivision/API calls after login */
export const getAdminCompanyCodeForData = (code, roleId, selectedAdminCompanyCode) => {
  if (String(code) !== "10") {
    return code;
  }
  if (isPitcAdminUser(code, roleId)) {
    return 10;
  }
  return selectedAdminCompanyCode || null;
};

/** Replace hardcoded MEPCO (15) defaults for code-10 users */
export const resolveOfficeCodeForAdmin = (offCode, selectedAdminCompanyCode) => {
  if (String(offCode) !== "10") {
    return offCode;
  }
  return selectedAdminCompanyCode || offCode;
};

/** Office code for paginated API calls (slices + forms) */
export const resolveApiOfficeCode = (
  code,
  roleId,
  officeCodeParam,
  selectedAdminCompanyCode,
) => {
  if (String(code) !== "10") {
    return code;
  }
  if (isPitcAdminUser(code, roleId)) {
    return 10;
  }
  return officeCodeParam || selectedAdminCompanyCode || code;
};

/** Default company dropdown value for code-10 admin screens */
export const getInitialAdminOfficeCode = (code, selectedAdminCompanyCode) => {
  if (String(code) !== "10") {
    return String(code ?? "");
  }
  return selectedAdminCompanyCode ? String(selectedAdminCompanyCode) : "";
};

/** Code-10 admin users must not load billing service table data */
export const isBillingTableFetchEnabled = (code) => String(code) !== "10";

/** Selected company for code-10 filters (dropdown, reports, tables) */
export const getEffectiveAdminCompanyCode = (
  offCode,
  selectedCompanyCode,
  selectedAdminCompanyCode,
) => {
  if (String(offCode) !== "10") {
    return offCode;
  }
  return selectedCompanyCode || selectedAdminCompanyCode || offCode;
};

/** Office code for report Go / API — respects hierarchy filters for code-10 admin */
export const resolveReportGoOfficeCode = ({
  code,
  officeCode,
  selectedCompanyCode,
  selectedAdminCompanyCode,
  circleCode,
  selectedDivision,
  selectedSubDivision,
}) => {
  if (String(code) !== "10") {
    return officeCode;
  }
  if (selectedSubDivision) return selectedSubDivision;
  if (selectedDivision) return selectedDivision;
  if (circleCode) return circleCode;
  const companyCode = getEffectiveAdminCompanyCode(
    code,
    selectedCompanyCode,
    selectedAdminCompanyCode,
  );
  return companyCode || officeCode;
};

/** CFC dashboard API — 2-digit company code → regional CFC id (e.g. 15 → 1500001) */
export const resolveCfcDashboardOfficeCode = (
  officeCodeParam,
  code,
  roleId,
  selectedAdminCompanyCode,
) => {
  let officeCode = officeCodeParam;
  if (String(code) === "10" && String(officeCode) === "10") {
    officeCode =
      getAdminCompanyCodeForData(code, roleId, selectedAdminCompanyCode) ||
      officeCode;
  }
  if (String(officeCode).length <= 2) {
    return `${String(officeCode).padStart(2, "0")}00001`;
  }
  return officeCode;
};

const DISCO_SHORT_NAMES = {
  11: "LESCO",
  12: "GEPCO",
  13: "FESCO",
  14: "IESCO",
  15: "MEPCO",
  26: "PESCO",
  27: "HAZECO",
  37: "HESCO",
  38: "SEPCO",
  48: "QESCO",
  59: "TESCO",
};

/** 2-digit DISCO code for logos/watermarks (admin code 10 → selected DISCO). */
export const resolveDiscoCompanyCodeForBranding = (
  userCode,
  roleId,
  selectedAdminCompanyCode,
) => {
  const resolved = String(
    getAdminCompanyCodeForData(userCode, roleId, selectedAdminCompanyCode) ??
      userCode,
  );
  if (resolved === "10") {
    return String(selectedAdminCompanyCode || DEFAULT_DISCO_COMPANY_CODE).slice(
      0,
      2,
    );
  }
  return resolved.slice(0, 2);
};

/** Extract 2-digit DISCO code from application id (e.g. CA-14111260616006 → 14). */
export const extractDiscoCodeFromApplicationId = (applicationId) => {
  if (!applicationId) return "";

  const afterHyphen = String(applicationId).trim().split("-")[1];
  if (afterHyphen && afterHyphen.length >= 2) {
    return afterHyphen.slice(0, 2);
  }

  return String(applicationId).slice(3, 5);
};

/**
 * DISCO code for document branding (logo, watermark, company name).
 * Admin code 10 → from application id; all other users → existing fallback.
 */
export const resolveDiscoCodeForDocument = (
  applicationId,
  userCode,
  roleId,
  selectedAdminCompanyCode,
  fallbackCompanyCode = "",
) => {
  if (String(userCode) === "10") {
    const discoFromApplication =
      extractDiscoCodeFromApplicationId(applicationId);
    if (discoFromApplication) {
      return discoFromApplication;
    }
  }

  if (
    fallbackCompanyCode !== null &&
    fallbackCompanyCode !== undefined &&
    fallbackCompanyCode !== ""
  ) {
    return String(fallbackCompanyCode).slice(0, 2);
  }

  return resolveDiscoCompanyCodeForBranding(
    userCode,
    roleId,
    selectedAdminCompanyCode,
  );
};

/** Canvas/PDF overlay label for uploaded documents. */
export const getDiscoUseOnlyWatermarkText = (companyCode) => {
  const discoCode = String(companyCode ?? "").slice(0, 2);
  const shortName = DISCO_SHORT_NAMES[discoCode] || "DISCO";
  return `For ${shortName} Use Only`;
};
